import Card from "@material-tailwind/react/Card";
import CardBody from "@material-tailwind/react/CardBody";
import React, { useEffect, useMemo, useState } from "react";
import {
  BiArrowBack,
  BiCheckSquare,
  BiDetail,
  BiEdit,
  BiPlus,
  BiPrinter,
  BiLockOpen,
} from "react-icons/bi";
import { BsFileEarmarkExcelFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";

import { Colors } from "../../assets/Colors";
import Spinner from "../spinner/Spinner";
import { maxPageGlobal } from "../../assets/config";
import {
  jobRequestTransaksiSelector,
  kirimJobRequest,
  kirimResetPassword,
} from "../../features/hrd/transaksi/job-request/jobRequestTransaksiSlice";
import {
  approvalSelector,
  approve,
} from "../../features/setup/approval/approvalSlice";
import Skeltons from "../loading/Skeltons";

// Fungsi untuk memformat tanggal ke format Indonesia (DD/MM/YYYY)
const formatDateToIndonesian = (date) => {
  if (!date) return "";

  try {
    // Normalisasi: ganti T jadi spasi, buang ms & timezone
    const clean = date.toString().replace("T", " ").split(".")[0].split("Z")[0];

    // expected: YYYY-MM-DD HH:mm:ss
    const [datePart, timePart = "00:00:00"] = clean.split(" ");
    if (!datePart) return "";

    const [year, month, day] = datePart.split("-");
    const [hours, minutes] = timePart.split(":");

    if (!year || !month || !day) return "";

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (err) {
    return "";
  }
};

export default function TableMemoReport(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filterText, setFilterText] = React.useState("");
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const [editEnabled, setEditEnabled] = React.useState(
    props.editEnabled ? props.editEnabled : false,
  );
  const [isSuccess, setisSuccess] = React.useState(false);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Success!", {
        position: "top-center",
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        theme: "light",
      });
      setisSuccess(false);
    }
  }, [isSuccess]);

  let data = props.tableData;
  if (props.usingMutation && !props.mutation.isLoading && data) {
    data = data.data;
  }

  // Filter data berdasarkan text search
  useEffect(() => {
    if (!filterText) {
      setFilteredData(data?.filter((item) => item.Approved === 99) || []);
      return;
    }

    if (!Array.isArray(data)) {
      setFilteredData([]);
      return;
    }

    const lowercasedFilter = filterText.toLowerCase();

    const filtered = data.filter((item) => {
      if (item.Approved !== 99) return false; // Hanya show Approved = 99

      // Cari di semua kolom yang mungkin
      return (
        // NoMemo (prioritas utama)
        (item.NoMemo && item.NoMemo.toLowerCase().includes(lowercasedFilter)) ||
        (item.NomorMemo &&
          item.NomorMemo.toLowerCase().includes(lowercasedFilter)) ||
        // From
        (item.From && item.From.toLowerCase().includes(lowercasedFilter)) ||
        // To - Array atau string
        (Array.isArray(item.ToList) &&
          item.ToList.some(
            (toItem) =>
              toItem.Name &&
              toItem.Name.toLowerCase().includes(lowercasedFilter),
          )) ||
        (item.To && item.To.toLowerCase().includes(lowercasedFilter)) ||
        // CC - Array atau string
        (Array.isArray(item.CcList) &&
          item.CcList.some(
            (ccItem) =>
              ccItem.Name &&
              ccItem.Name.toLowerCase().includes(lowercasedFilter),
          )) ||
        (item.Cc && item.Cc.toLowerCase().includes(lowercasedFilter)) ||
        // Subject
        (item.Subject &&
          item.Subject.toLowerCase().includes(lowercasedFilter)) ||
        // Created By
        (item.CreatedBy &&
          item.CreatedBy.toLowerCase().includes(lowercasedFilter)) ||
        // Status
        (item.Approved !== undefined &&
          (item.Approved === 99 ? "close" : "open").includes(
            lowercasedFilter,
          )) ||
        // Format tanggal untuk pencarian
        (item.Date &&
          formatDateToIndonesian(item.Date)
            .toLowerCase()
            .includes(lowercasedFilter)) ||
        (item.DateEnd &&
          formatDateToIndonesian(item.DateEnd)
            .toLowerCase()
            .includes(lowercasedFilter)) ||
        // Day Diff (konversi ke string)
        (item.Datediff !== undefined &&
          item.Datediff.toString().includes(lowercasedFilter)) ||
        // ToNik jika string atau array
        (item.ToNik &&
          (Array.isArray(item.ToNik)
            ? item.ToNik.some((nik) =>
                nik.toString().toLowerCase().includes(lowercasedFilter),
              )
            : item.ToNik.toString()
                .toLowerCase()
                .includes(lowercasedFilter))) ||
        // FromNik
        (item.FromNik &&
          item.FromNik.toString().toLowerCase().includes(lowercasedFilter))
      );
    });

    setFilteredData(filtered);
  }, [filterText, data]);

  const columns = [
    {
      name: "No",
      cell: (row, index) => index + 1,
      width: "70px",
      sortable: true,
    },
    {
      name: "Memo Number",
      selector: (row) => row.NoMemo || row.NomorMemo || "-",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="font-mono text-sm">
          {/* {row.NoMemo || row.NomorMemo || "-"} */}
          {row.NoMemo && row.NomorMemo && row.NoMemo !== row.NomorMemo && (
            <div className="text-xs text-gray-500">{row.NomorMemo}</div>
          )}
        </div>
      ),
      width: "180px",
    },
    {
      name: "From",
      selector: (row) => row.From || "-",
      sortable: true,
    },
    {
      name: "To",
      selector: (row) => {
        if (Array.isArray(row.ToList)) {
          return row.ToList.map((item) => item.Name ?? "").join(", ");
        }
        return row.To ?? "-";
      },
      sortable: true,
      wrap: true,
    },
    {
      name: "Cc",
      selector: (row) =>
        Array.isArray(row.CcList)
          ? row.CcList.map((item) => item.Name || "").join(", ")
          : row.Cc || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Start Date",
      selector: (row) => formatDateToIndonesian(row.Date),
      sortable: true,
      width: "150px",
    },
    {
      name: "End Date",
      selector: (row) => formatDateToIndonesian(row.DateEnd),
      sortable: true,
      width: "150px",
    },
    {
      name: "Day Diff",
      selector: (row) => row.Datediff || 0,
      sortable: true,
      width: "100px",
    },
    {
      name: "Subject",
      selector: (row) => row.Subject || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Created By",
      selector: (row) => row.CreatedBy || "-",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.Approved === 99 ? "Close" : "Open"),
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            row.Approved === 99
              ? "bg-green-100 text-green-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {row.Approved === 99 ? "Close" : "Open"}
        </span>
      ),
      width: "100px",
    },
    {
      name: "Actions",
      cell: (row) => {
        const userString = localStorage.getItem("user");
        let authUser = null;

        if (userString) {
          try {
            const userObj = JSON.parse(userString);
            authUser = userObj?.Nik;
          } catch (err) {
            console.error("Failed to parse user from localStorage", err);
          }
        }

        const isTo = row.IsTo || false;

        return (
          <div className="flex space-x-2">
            {isTo && row.Approved < 1 && (
              <button
                onClick={() =>
                  navigate(`/memo/internalmemodetail/action/${row.ID}`)
                }
                className="flex flex-row items-center bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 border border-blue-500 rounded shadow text-sm"
              >
                <span>Action</span>
              </button>
            )}
            {props.isReport && row.Approved === 99 && (
              <button
                onClick={() => navigate(`/memo/report/pdf/${row.ID}`)}
                className="flex flex-row items-center bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-3 border border-green-500 rounded shadow text-sm"
              >
                <span>PDF</span>
              </button>
            )}

            <button
              onClick={() =>
                navigate(`/memo/internalmemodetail/view/${row.ID}`)
              }
              className="flex flex-row items-center bg-gray-500 hover:bg-gray-600 text-white font-semibold py-1 px-3 border border-gray-500 rounded shadow text-sm"
            >
              <span>View</span>
            </button>
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "180px",
    },
  ];

  const subHeaderComponentMemo = useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    return (
      <div className="w-full mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0">
          <div className="flex items-center space-x-2 w-full md:w-auto">
            <input
              type="text"
              placeholder="Search memo number, subject, from, to..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-80"
            />
            {filterText && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>

          {props.addButtonTambah && (
            <button
              onClick={() => props.OnClick({})}
              className="flex flex-row items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition duration-200 ease-in-out"
            >
              <BiPlus className="text-xl text-white mr-2" />
              Tambah Memo
            </button>
          )}
        </div>
        {filterText && (
          <div className="text-sm text-gray-500 mt-2">
            Found {filteredData.length} records matching "{filterText}"
          </div>
        )}
      </div>
    );
  }, [
    filterText,
    resetPaginationToggle,
    filteredData.length,
    props.addButtonTambah,
  ]);

  return (
    <Card className="flex flex-col items-center content-center">
      {props.fetching ? (
        <div className="flex items-center py-8">
          <Spinner />
        </div>
      ) : (
        <CardBody className="w-full pt-4">
          {Array.isArray(data) && data.length > 0 ? (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              paginationResetDefaultPage={resetPaginationToggle}
              subHeader
              subHeaderComponent={subHeaderComponentMemo}
              persistTableHead
              progressPending={props.usingMutation && props.mutation.isLoading}
              progressComponent={<Skeltons />}
              noDataComponent={
                <div className="w-full flex flex-col justify-center items-center py-8">
                  <BsFileEarmarkExcelFill className="text-7xl text-gray-400" />
                  <div className="pb-4 text-gray-800 mt-4">
                    No matching closed memos found
                  </div>
                  <div className="text-gray-500 text-sm">
                    Try different keywords or check if memos are closed
                  </div>
                </div>
              }
              highlightOnHover
              pointerOnHover
              customStyles={{
                headCells: {
                  style: {
                    color: Colors.primary,
                    fontWeight: "bold",
                    fontSize: "14px",
                    backgroundColor: "#f8fafc",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  },
                },
                cells: {
                  style: {
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    fontSize: "14px",
                    verticalAlign: "top",
                  },
                },
                rows: {
                  style: {
                    minHeight: "60px",
                  },
                },
              }}
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                rowsPerPageText: "Baris per halaman:",
                rangeSeparatorText: "dari",
              }}
            />
          ) : (
            <div className="w-full flex flex-col justify-center items-center py-8 border border-dashed border-gray-300 rounded-lg">
              <BsFileEarmarkExcelFill className="text-7xl text-gray-400 mb-4" />
              <div className="pb-4 text-gray-600 text-lg">
                Tidak ada data memo yang sudah close
              </div>
              <div className="text-gray-500 text-sm mb-4">
                Hanya memo dengan status "Close" yang ditampilkan di report
              </div>
              {props.addButtonTambah && (
                <button
                  onClick={() => props.OnClick({})}
                  className="flex flex-row items-center justify-center bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg shadow transition duration-200 ease-in-out"
                >
                  <BiPlus className="text-xl text-white mr-2" />
                  Buat Memo Baru
                </button>
              )}
            </div>
          )}
        </CardBody>
      )}
    </Card>
  );
}
